import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import toast from "react-hot-toast";
import { privateRequest } from "services/privateRequest";
import { TEST_CASE_RUNS, TEST_CASE_RUN_SUMMARY, TEST_CASE_STATUS_HISTORY, TEST_CASE_RUN_STATUS_HISTORY, TEST_CASE_RUN_ACTIVITY_SUMMARY } from "services/endPoints";

const initialState = {
  testRunsList: [],
  testRunDetails: null,
  runTestCases: [],
  runSectionsList: [],
  runSectionCasesMap: {},
  runGraphSummary: null,
  testCaseStatusHistory: [],
  testCaseStatusHistoryTotal: 0,
  testCaseStatusHistoryPage: 0,
  testRunsLoading: false,
  testRunDetailsLoading: false,
  runTestCasesLoading: false,
  runSectionsLoading: false,
  runGraphSummaryLoading: false,
  testCaseStatusHistoryLoading: false,
  testCaseStatusHistoryLoadingMore: false,
  runStatusActivity: [],
  runStatusActivityTotal: 0,
  runStatusActivityPage: 0,
  runStatusActivityLoading: false,
  runStatusActivityLoadingMore: false,
  runStatusActivitySummary: null,
  runStatusActivitySummaryLoading: false,
  createTestRunLoading: false,
  updateTestRunLoading: false,
  deleteTestRunLoading: false,
  updateRunStatusLoading: false,
  testRunsTotalCount: 0,
  runTestCasesTotalCount: 0,
};

const extractList = (res) => {
  const root = res?.data?.data ?? res?.data ?? {};

  if (Array.isArray(root)) {
    return { data: root, count: { total: root.length } };
  }

  const data = Array.isArray(root?.data)
    ? root.data
    : Array.isArray(root?.content)
      ? root.content
      : [];

  const total =
    root?.count?.total ??
    root?.count?.Total ??
    root?.totalElements ??
    root?.total ??
    root?.page?.totalElements ??
    data.length;

  return {
    data,
    count: { total: Number(total) || 0 },
  };
};

export const getTestRuns = createAsyncThunk(
  "testRun/getTestRuns",
  async (
    { page = 0, size = 20, append = false, ...rest } = {},
    { rejectWithValue }
  ) => {
    try {
      const res = await privateRequest.get(TEST_CASE_RUNS, {
        params: { page, size, ...rest },
      });
      const parsed = extractList(res);
      return { ...parsed, append: Boolean(append) };
    } catch (error) {
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const getTestRunById = createAsyncThunk(
  "testRun/getTestRunById",
  async (id, { rejectWithValue }) => {
    try {
      const res = await privateRequest.get(`${TEST_CASE_RUNS}/${id}`);
      return res?.data?.data || res?.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data);
    }
  }
);

/** GET /api/test-cases/{run-id}/summary — status counts for run graph */
export const getTestRunGraphSummary = createAsyncThunk(
  "testRun/getTestRunGraphSummary",
  async (runId, { rejectWithValue }) => {
    try {
      if (runId == null || runId === "") {
        return rejectWithValue({ message: "runId is required" });
      }
      const res = await privateRequest.get(TEST_CASE_RUN_SUMMARY(runId));
      return res?.data?.data ?? res?.data ?? null;
    } catch (error) {
      return rejectWithValue(error?.response?.data);
    }
  }
);

const compactParams = (params = {}) =>
  Object.fromEntries(
    Object.entries(params).filter(
      ([, value]) => value != null && value !== ""
    )
  );

/**
 * GET /api/test-cases/runs/{runId}/sections
 * Paginated API — FE loads all pages (no section UI pagination).
 * Includes testCaseCount for run-scoped tree / expand behavior.
 */
export const getSectionsByRunId = createAsyncThunk(
  "testRun/getSectionsByRunId",
  async ({ runId, size = 100 } = {}, { rejectWithValue }) => {
    try {
      if (runId == null || runId === "") {
        return rejectWithValue({ message: "runId is required" });
      }

      const collected = [];
      let currentPage = 1;
      let total = 0;

      for (;;) {
        const res = await privateRequest.get(
          `${TEST_CASE_RUNS}/${runId}/sections`,
          {
            params: {
              page: currentPage,
              size,
              sortBy: "id",
              direction: "asc",
            },
          }
        );
        const parsed = extractList(res);
        collected.push(...(parsed.data || []));
        total = parsed.count?.total || collected.length;
        if (
          !parsed.data?.length ||
          collected.length >= total ||
          currentPage >= 50
        ) {
          break;
        }
        currentPage += 1;
      }

      return collected;
    } catch (error) {
      return rejectWithValue(error?.response?.data);
    }
  }
);

/** GET /api/test-cases/runs/{runId}/test-cases — full list (edit-run prefill). */
export const getTestCasesByRunId = createAsyncThunk(
  "testRun/getTestCasesByRunId",
  async ({ runId, page = 1, size = 100, ...filters } = {}, { rejectWithValue }) => {
    try {
      if (runId == null || runId === "") {
        return rejectWithValue({ message: "runId is required" });
      }

      const collected = [];
      let currentPage = page;
      let total = 0;

      for (;;) {
        const res = await privateRequest.get(
          `${TEST_CASE_RUNS}/${runId}/test-cases`,
          {
            params: compactParams({
              page: currentPage,
              size,
              sortBy: "id",
              direction: "desc",
              ...filters,
            }),
          }
        );
        const parsed = extractList(res);
        collected.push(...(parsed.data || []));
        total = parsed.count?.total || collected.length;
        if (
          !parsed.data?.length ||
          collected.length >= total ||
          currentPage >= 50
        ) {
          break;
        }
        currentPage += 1;
      }

      return { data: collected, count: { total } };
    } catch (error) {
      return rejectWithValue(error?.response?.data);
    }
  }
);

const emptyRunSectionState = () => ({
  list: [],
  total: 0,
  page: 0,
  size: 20,
  loading: false,
  loadingMore: false,
  loaded: false,
});

/** Per-section page for Test Run accordion. Does not touch Test Cases store. */
export const getRunSectionCases = createAsyncThunk(
  "testRun/getRunSectionCases",
  async (
    {
      runId,
      sectionId,
      page = 1,
      size = 20,
      append = false,
      ...filters
    } = {},
    { rejectWithValue }
  ) => {
    try {
      if (runId == null || runId === "") {
        return rejectWithValue({ message: "runId is required" });
      }
      if (sectionId == null || sectionId === "") {
        return rejectWithValue({ message: "sectionId is required" });
      }

      const res = await privateRequest.get(
        `${TEST_CASE_RUNS}/${runId}/test-cases`,
        {
          params: compactParams({
            sectionId,
            page,
            size,
            sortBy: "id",
            direction: "desc",
            ...filters,
          }),
        }
      );
      const parsed = extractList(res);
      return {
        sectionId: String(sectionId),
        data: parsed.data || [],
        total: parsed.count?.total || 0,
        page: Number(page) || 1,
        size: Number(size) || 20,
        append: Boolean(append),
      };
    } catch (error) {
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const createTestRun = createAsyncThunk(
  "testRun/createTestRun",
  async (payload, { rejectWithValue }) => {
    try {
      const res = await privateRequest.post(TEST_CASE_RUNS, payload);
      if (res) {
        toast?.success?.(
          res?.data?.message || "Test run created successfully."
        );
      }
      return res?.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const updateTestRun = createAsyncThunk(
  "testRun/updateTestRun",
  async ({ id, payload }, { rejectWithValue }) => {
    try {
      const res = await privateRequest.put(`${TEST_CASE_RUNS}/${id}`, payload);
      if (res) {
        toast?.success?.(
          res?.data?.message || "Test run updated successfully."
        );
      }
      return res?.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const deleteTestRun = createAsyncThunk(
  "testRun/deleteTestRun",
  async (id, { rejectWithValue }) => {
    try {
      const res = await privateRequest.delete(`${TEST_CASE_RUNS}/${id}`);
      if (res) {
        toast?.success?.(
          res?.data?.message || "Test run deleted successfully."
        );
      }
      return { id, data: res?.data };
    } catch (error) {
      return rejectWithValue(error?.response?.data);
    }
  }
);

/** GET /api/test-cases/{testCaseId}/test-run/{testRunId}/test-case-status-history */
export const getTestCaseStatusHistory = createAsyncThunk(
  "testRun/getTestCaseStatusHistory",
  async (
    {
      testCaseId,
      testRunId,
      runId,
      page = 0,
      size = 20,
      append = false,
    } = {},
    { rejectWithValue }
  ) => {
    try {
      const resolvedRunId = testRunId ?? runId;
      if (testCaseId == null || testCaseId === "") {
        return rejectWithValue({ message: "testCaseId is required" });
      }
      if (resolvedRunId == null || resolvedRunId === "") {
        return rejectWithValue({ message: "testRunId is required" });
      }
      const res = await privateRequest.get(
        TEST_CASE_STATUS_HISTORY(testCaseId, resolvedRunId),
        { params: { page, size } }
      );
      const parsed = extractList(res);
      return { ...parsed, append, page: Number(page) || 0 };
    } catch (error) {
      return rejectWithValue(error?.response?.data);
    }
  }
);

/** GET /api/test-cases/test-run/{testRunId}/test-case-status-history — paginated list */
export const getTestRunStatusActivity = createAsyncThunk(
  "testRun/getTestRunStatusActivity",
  async (
    { testRunId, runId, page = 0, size = 20, append = false } = {},
    { rejectWithValue }
  ) => {
    try {
      const resolvedRunId = testRunId ?? runId;
      if (resolvedRunId == null || resolvedRunId === "") {
        return rejectWithValue({ message: "testRunId is required" });
      }
      const res = await privateRequest.get(
        TEST_CASE_RUN_STATUS_HISTORY(resolvedRunId),
        { params: { page, size } }
      );
      const parsed = extractList(res);
      return { ...parsed, append, page: Number(page) || 0 };
    } catch (error) {
      return rejectWithValue(error?.response?.data);
    }
  }
);

/** GET /api/test-cases/test-run/{testRunId}/dashboard/activity-summary?days=N */
export const getTestRunActivitySummary = createAsyncThunk(
  "testRun/getTestRunActivitySummary",
  async (
    { testRunId, runId, days } = {},
    { rejectWithValue }
  ) => {
    try {
      const resolvedRunId = testRunId ?? runId;
      if (resolvedRunId == null || resolvedRunId === "") {
        return rejectWithValue({ message: "testRunId is required" });
      }
      const params = {};
      if (days != null && days !== "") {
        params.days = Number(days) || 14;
      }
      const res = await privateRequest.get(
        TEST_CASE_RUN_ACTIVITY_SUMMARY(resolvedRunId),
        { params }
      );
      const root = res?.data?.data ?? res?.data ?? {};
      return {
        days: Number(root.days) || params.days || 14,
        passed: Number(root.passed) || 0,
        failed: Number(root.failed) || 0,
        pending: Number(root.pending) || 0,
        draft: Number(root.draft) || 0,
        total: Number(root.total) || 0,
        trends: Array.isArray(root.trends)
          ? root.trends
          : Array.isArray(root.trend)
            ? root.trend
            : [],
      };
    } catch (error) {
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const updateTestCaseStatusInRun = createAsyncThunk(
  "testRun/updateTestCaseStatusInRun",
  async (
    { runId, testCaseId, formData, status },
    { rejectWithValue }
  ) => {
    try {
      if (runId == null || testCaseId == null) {
        return rejectWithValue({ message: "runId and testCaseId are required" });
      }

      // Swagger: PUT /test-cases/runs/{runId}/test-cases/{testCaseId}/status
      const res = await privateRequest.put(
        `${TEST_CASE_RUNS}/${runId}/test-cases/${testCaseId}/status`,
        formData
      );
      if (res) {
        toast?.success?.(
          res?.data?.message || "Status updated successfully."
        );
      }
      return {
        runId,
        testCaseId,
        status,
        data: res?.data,
      };
    } catch (error) {
      return rejectWithValue(error?.response?.data);
    }
  }
);

const testRunSlice = createSlice({
  name: "testRun",
  initialState,
  reducers: {
    clearTestRunDetails: (state) => {
      state.testRunDetails = null;
      state.runTestCases = [];
      state.runTestCasesTotalCount = 0;
      state.runSectionsList = [];
      state.runSectionCasesMap = {};
      state.runGraphSummary = null;
      state.runGraphSummaryLoading = false;
      state.runStatusActivity = [];
      state.runStatusActivityTotal = 0;
      state.runStatusActivityPage = 0;
      state.runStatusActivityLoading = false;
      state.runStatusActivityLoadingMore = false;
      state.runStatusActivitySummary = null;
      state.runStatusActivitySummaryLoading = false;
    },
    clearRunSectionCasesMap: (state) => {
      state.runSectionCasesMap = {};
    },
    clearTestCaseStatusHistory: (state) => {
      state.testCaseStatusHistory = [];
      state.testCaseStatusHistoryTotal = 0;
      state.testCaseStatusHistoryPage = 0;
      state.testCaseStatusHistoryLoading = false;
      state.testCaseStatusHistoryLoadingMore = false;
    },
    hydrateRunSectionCasesMap: (state, action) => {
      const grouped = action.payload || {};
      const next = {};
      Object.entries(grouped).forEach(([sectionId, list]) => {
        const rows = Array.isArray(list) ? list : [];
        next[String(sectionId)] = {
          list: rows,
          total: rows.length,
          page: 1,
          size: rows.length || 20,
          loading: false,
          loadingMore: false,
          loaded: true,
        };
      });
      state.runSectionCasesMap = next;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getTestRuns.pending, (state, action) => {
      // Keep list visible while infinite-scrolling on mobile
      if (!action.meta?.arg?.append) {
        state.testRunsLoading = true;
      }
    });
    builder.addCase(getTestRuns.fulfilled, (state, action) => {
      state.testRunsLoading = false;
      const incoming = action.payload.data || [];
      const append = Boolean(action.payload.append);
      if (append) {
        const existingIds = new Set(
          (state.testRunsList || []).map((item) => String(item?.id ?? item?._id))
        );
        state.testRunsList = [
          ...(state.testRunsList || []),
          ...incoming.filter(
            (item) => !existingIds.has(String(item?.id ?? item?._id))
          ),
        ];
      } else {
        state.testRunsList = incoming;
      }
      state.testRunsTotalCount = action.payload.count?.total || 0;
    });
    builder.addCase(getTestRuns.rejected, (state) => {
      state.testRunsLoading = false;
    });

    builder.addCase(getTestRunById.pending, (state) => {
      state.testRunDetailsLoading = true;
      state.testRunDetails = null;
    });
    builder.addCase(getTestRunById.fulfilled, (state, action) => {
      state.testRunDetailsLoading = false;
      state.testRunDetails = action.payload;
    });
    builder.addCase(getTestRunById.rejected, (state) => {
      state.testRunDetailsLoading = false;
    });

    builder.addCase(getTestRunGraphSummary.pending, (state) => {
      state.runGraphSummaryLoading = true;
    });
    builder.addCase(getTestRunGraphSummary.fulfilled, (state, action) => {
      state.runGraphSummaryLoading = false;
      state.runGraphSummary = action.payload;
    });
    builder.addCase(getTestRunGraphSummary.rejected, (state) => {
      state.runGraphSummaryLoading = false;
      state.runGraphSummary = null;
    });

    builder.addCase(getSectionsByRunId.pending, (state) => {
      state.runSectionsLoading = true;
    });
    builder.addCase(getSectionsByRunId.fulfilled, (state, action) => {
      state.runSectionsLoading = false;
      state.runSectionsList = action.payload || [];
    });
    builder.addCase(getSectionsByRunId.rejected, (state) => {
      state.runSectionsLoading = false;
      state.runSectionsList = [];
    });

    builder.addCase(getTestCasesByRunId.pending, (state) => {
      state.runTestCasesLoading = true;
    });
    builder.addCase(getTestCasesByRunId.fulfilled, (state, action) => {
      state.runTestCasesLoading = false;
      state.runTestCases = action.payload.data || [];
      state.runTestCasesTotalCount = action.payload.count?.total || 0;
    });
    builder.addCase(getTestCasesByRunId.rejected, (state) => {
      state.runTestCasesLoading = false;
      state.runTestCases = [];
      state.runTestCasesTotalCount = 0;
    });

    builder.addCase(getRunSectionCases.pending, (state, action) => {
      const sectionId = String(action.meta?.arg?.sectionId ?? "");
      const append = Boolean(action.meta?.arg?.append);
      if (!sectionId) return;
      const prev = state.runSectionCasesMap[sectionId] || emptyRunSectionState();
      state.runSectionCasesMap[sectionId] = {
        ...prev,
        loading: !append,
        loadingMore: append,
      };
    });
    builder.addCase(getRunSectionCases.fulfilled, (state, action) => {
      const { sectionId, data, total, page, size, append } = action.payload;
      const prev = state.runSectionCasesMap[sectionId] || emptyRunSectionState();
      const incoming = data || [];
      const nextList = append
        ? [
            ...(prev.list || []),
            ...incoming.filter((item) => {
              const id = String(item?.id ?? item?.testCaseId);
              return !(prev.list || []).some(
                (existing) => String(existing?.id ?? existing?.testCaseId) === id
              );
            }),
          ]
        : incoming;
      state.runSectionCasesMap[sectionId] = {
        list: nextList,
        total,
        page,
        size,
        loading: false,
        loadingMore: false,
        loaded: true,
      };
    });
    builder.addCase(getRunSectionCases.rejected, (state, action) => {
      const sectionId = String(action.meta?.arg?.sectionId ?? "");
      if (!sectionId) return;
      const prev = state.runSectionCasesMap[sectionId] || emptyRunSectionState();
      state.runSectionCasesMap[sectionId] = {
        ...prev,
        loading: false,
        loadingMore: false,
        loaded: true,
      };
    });

    builder.addCase(createTestRun.pending, (state) => {
      state.createTestRunLoading = true;
    });
    builder.addCase(createTestRun.fulfilled, (state) => {
      state.createTestRunLoading = false;
    });
    builder.addCase(createTestRun.rejected, (state) => {
      state.createTestRunLoading = false;
    });

    builder.addCase(updateTestRun.pending, (state) => {
      state.updateTestRunLoading = true;
    });
    builder.addCase(updateTestRun.fulfilled, (state) => {
      state.updateTestRunLoading = false;
    });
    builder.addCase(updateTestRun.rejected, (state) => {
      state.updateTestRunLoading = false;
    });

    builder.addCase(deleteTestRun.pending, (state) => {
      state.deleteTestRunLoading = true;
    });
    builder.addCase(deleteTestRun.fulfilled, (state, action) => {
      state.deleteTestRunLoading = false;
      const deletedId = action.payload?.id;
      if (deletedId == null) return;
      state.testRunsList = (state.testRunsList || []).filter(
        (item) => String(item?.id ?? item?._id) !== String(deletedId)
      );
      state.testRunsTotalCount = Math.max(0, (state.testRunsTotalCount || 0) - 1);
    });
    builder.addCase(deleteTestRun.rejected, (state) => {
      state.deleteTestRunLoading = false;
    });

    builder.addCase(getTestCaseStatusHistory.pending, (state, action) => {
      const append = Boolean(action.meta?.arg?.append);
      if (append) {
        state.testCaseStatusHistoryLoadingMore = true;
      } else {
        state.testCaseStatusHistoryLoading = true;
      }
    });
    builder.addCase(getTestCaseStatusHistory.fulfilled, (state, action) => {
      const append = Boolean(action.payload.append);
      const incoming = action.payload.data || [];
      state.testCaseStatusHistoryLoading = false;
      state.testCaseStatusHistoryLoadingMore = false;
      state.testCaseStatusHistoryTotal = action.payload.count?.total || 0;
      state.testCaseStatusHistoryPage = action.payload.page ?? 0;
      if (append) {
        const historyKey = (item) =>
          String(
            item?.id ??
              `${item?.createdAt ?? ""}-${item?.testCaseStatus ?? ""}`
          );
        const existingKeys = new Set(
          (state.testCaseStatusHistory || []).map(historyKey)
        );
        state.testCaseStatusHistory = [
          ...(state.testCaseStatusHistory || []),
          ...incoming.filter((item) => !existingKeys.has(historyKey(item))),
        ];
      } else {
        state.testCaseStatusHistory = incoming;
      }
    });
    builder.addCase(getTestCaseStatusHistory.rejected, (state) => {
      state.testCaseStatusHistoryLoading = false;
      state.testCaseStatusHistoryLoadingMore = false;
    });

    builder.addCase(getTestRunStatusActivity.pending, (state, action) => {
      const append = Boolean(action.meta?.arg?.append);
      if (append) {
        state.runStatusActivityLoadingMore = true;
      } else {
        state.runStatusActivityLoading = true;
      }
    });
    builder.addCase(getTestRunStatusActivity.fulfilled, (state, action) => {
      const append = Boolean(action.payload.append);
      const incoming = action.payload.data || [];
      state.runStatusActivityLoading = false;
      state.runStatusActivityLoadingMore = false;
      state.runStatusActivityTotal = action.payload.count?.total || 0;
      state.runStatusActivityPage = action.payload.page ?? 0;
      if (append) {
        const activityKey = (item) =>
          String(
            item?.id ??
              `${item?.createdAt ?? ""}-${item?.testCase?.id ?? ""}-${item?.testCaseStatus ?? ""}`
          );
        const existingKeys = new Set(
          (state.runStatusActivity || []).map(activityKey)
        );
        state.runStatusActivity = [
          ...(state.runStatusActivity || []),
          ...incoming.filter((item) => !existingKeys.has(activityKey(item))),
        ];
      } else {
        state.runStatusActivity = incoming;
      }
    });
    builder.addCase(getTestRunStatusActivity.rejected, (state) => {
      state.runStatusActivityLoading = false;
      state.runStatusActivityLoadingMore = false;
    });

    builder.addCase(getTestRunActivitySummary.pending, (state) => {
      state.runStatusActivitySummaryLoading = true;
    });
    builder.addCase(getTestRunActivitySummary.fulfilled, (state, action) => {
      state.runStatusActivitySummaryLoading = false;
      state.runStatusActivitySummary = action.payload;
    });
    builder.addCase(getTestRunActivitySummary.rejected, (state) => {
      state.runStatusActivitySummaryLoading = false;
      state.runStatusActivitySummary = null;
    });

    builder.addCase(updateTestCaseStatusInRun.pending, (state) => {
      state.updateRunStatusLoading = true;
    });
    builder.addCase(updateTestCaseStatusInRun.fulfilled, (state, action) => {
      state.updateRunStatusLoading = false;
      const { testCaseId, testCaseRunId, status } = action.payload || {};
      if (testCaseId == null && testCaseRunId == null) return;

      const patchItem = (item) => {
        const rowCaseId = item?.testCaseId ?? item?.id;
        const rowRunId = item?.testCaseRunId ?? item?.id;
        const matchesCase =
          testCaseId != null && String(rowCaseId) === String(testCaseId);
        const matchesRun =
          testCaseRunId != null && String(rowRunId) === String(testCaseRunId);
        if (!matchesCase && !matchesRun) return;
        item.status = status;
        item.testCaseStatus = status;
      };

      (state.runTestCases || []).forEach(patchItem);
      Object.values(state.runSectionCasesMap || {}).forEach((sectionState) => {
        (sectionState?.list || []).forEach(patchItem);
      });

      const details = state.testRunDetails;
      if (!details) return;
      [
        details.testCaseStatusMappings,
        details.testCases,
        details.testCaseResults,
        details.results,
        details.data,
      ]
        .filter(Array.isArray)
        .forEach((list) => list.forEach(patchItem));
    });
    builder.addCase(updateTestCaseStatusInRun.rejected, (state) => {
      state.updateRunStatusLoading = false;
    });
  },
});

export const {
  clearTestRunDetails,
  clearRunSectionCasesMap,
  hydrateRunSectionCasesMap,
  clearTestCaseStatusHistory,
} = testRunSlice.actions;

export default testRunSlice.reducer;
